/* MOON BASIN WORLDS - LIVING CANVAS GALLERY controller.
   Simon, 2026-09-07. Ported from the Art That Tells Stories gallery
   (ats-site/src/components/Gallery.astro) plus this theme's own precedents:
   paginated-list.js (observer), layered-slideshow.js (play/pause swap),
   announcement-bar.js (tab-visibility pause).

   Rules it enforces:
     - a clip's src is attached the FIRST time its tile comes into view,
       never on page load (preload="none" + data-clip)
     - only tiles on screen play; leaving the viewport pauses
     - at most MAX_PLAYING decode at once
     - the tab going hidden pauses everything; coming back resumes what is
       in view
     - prefers-reduced-motion: no src is ever attached, posters stand in
     - no context menu, no drag inside the grid (friction, not a lock)
     - back-navigation returns the visitor to where they tapped
*/
(() => {
  const init = () => {
    const root = document.querySelector('[data-mb-canvas-gallery]');
    if (!root) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const videos = Array.from(root.querySelectorAll('video[data-clip]'));
    const status = root.querySelector('[data-mb-canvas-status]');
    // 2026-09-09 (Simon), on Jay: "on the mobile, it kind of flickers when I
    // scroll" / "Flick seems worse". A phone has far fewer hardware video
    // decoders than a desktop and iOS Safari blanks a tile when it runs out,
    // so the cap is 3 on touch devices. Play/pause churn at the viewport edge
    // is the other flicker source: fill() is settled 150 ms after the last
    // observer change, and a tile has to be half on screen before it counts.
    const touch = window.matchMedia('(hover: none)').matches;
    const MAX_PLAYING = touch ? 3 : 6;
    const playing = new Set();

    // 2026-09-27 (Simon), Jay on Sunday cell service: the 48 posters (960px,
    // about 250 KB each, 12 MB in all) loaded at once on every homepage visit
    // and buried the rotation's shirt image. Posters are now 480px, and only
    // the first row carries a poster attribute; the rest hold data-poster and
    // receive it as they come within 1.5 screens, or the moment their clip is
    // warmed. Under prefers-reduced-motion the poster is the whole tile, so the
    // observer runs there too.
    const attachPoster = (v) => {
      if (v && !v.getAttribute('poster') && v.dataset.poster) v.setAttribute('poster', v.dataset.poster);
    };
    const lazyPosters = videos.filter((v) => v.dataset.poster);
    if (lazyPosters.length) {
      if ('IntersectionObserver' in window) {
        const pio = new IntersectionObserver((entries) => {
          for (const e of entries) {
            if (e.isIntersecting) { attachPoster(e.target); pio.unobserve(e.target); }
          }
        }, { rootMargin: '150% 0px 150% 0px', threshold: 0 });
        lazyPosters.forEach((v) => pio.observe(v));
      } else {
        lazyPosters.forEach(attachPoster);
      }
    }

    const play = (v) => {
      if (playing.has(v)) return;
      if (playing.size >= MAX_PLAYING) return;
      attachPoster(v);
      if (!v.getAttribute('src')) {
        v.src = v.dataset.clip;
        v.load();
      }
      // Reserve the slot before play() resolves: one observer callback can
      // ask for nine tiles at once, and the promise-based add let all nine
      // through the cap (seen 2026-09-07, 9 decoding on a 1280px desktop).
      playing.add(v);
      const start = () => {
        const p = v.play();
        if (p && typeof p.catch === 'function') {
          p.catch(() => { playing.delete(v); });
        }
      };
      // Touch (Jay 2026-09-09: the flash "seemed to only do it on the first
      // one"): calling play() before the clip has decoded frames is what
      // shows poster, then black, then motion. Wait for canplay, then start.
      if (touch && v.readyState < 3) {
        v.addEventListener('canplay', () => { if (playing.has(v)) start(); }, { once: true });
      } else {
        start();
      }
    };

    const pause = (v) => {
      if (!v.paused) v.pause();
      playing.delete(v);
    };

    if (!reduced && videos.length && 'IntersectionObserver' in window) {
      // `visible` is the truth; `fill()` keeps the closest-to-centre visible
      // tiles playing up to the cap and pauses the rest. A tile refused at
      // the cap is retried the moment a slot frees, so a full-width row is
      // never left frozen (seen 2026-09-07: six tiles never played after the
      // cap started holding).
      const visible = new Set();
      const fill = () => {
        const mid = window.innerHeight / 2;
        const want = Array.from(visible).sort((a, b) => {
          const ra = a.getBoundingClientRect(), rb = b.getBoundingClientRect();
          return Math.abs(ra.top + ra.height / 2 - mid) - Math.abs(rb.top + rb.height / 2 - mid);
        });
        const keep = new Set(want.slice(0, MAX_PLAYING));
        videos.forEach((v) => { if (!keep.has(v)) pause(v); });
        keep.forEach(play);
      };
      let fillTimer = 0;
      // 2026-09-09 (Simon), Jay on his iPhone: "the live art when you scroll
      // down it flickers, rotation is fine". Each tile used to get its file
      // and start playing at the moment it crossed into view, so the phone
      // showed poster, then black, then motion, tile after tile as the thumb
      // moved. On touch: (1) a tile that is within one screen of the viewport
      // fetches its clip early, without playing, so the first frame is ready
      // before it arrives; (2) it starts once it is half on screen and stops
      // only once it has fully left (hysteresis, so a tile hovering at the
      // edge is never toggled); (3) play/pause is settled 150 ms after the
      // last change. Desktop keeps the original single-threshold behaviour.
      const warm = (v) => {
        attachPoster(v);
        if (v.getAttribute('src')) return;
        v.preload = 'auto';
        v.src = v.dataset.clip;
        v.load();
      };
      if (touch) {
        const pre = new IntersectionObserver((entries) => {
          for (const e of entries) if (e.isIntersecting) warm(e.target);
        }, { rootMargin: '100% 0px 100% 0px', threshold: 0 });
        videos.forEach((v) => pre.observe(v));
      }
      const io = new IntersectionObserver((entries) => {
        for (const e of entries) {
          if (touch) {
            if (e.intersectionRatio >= 0.5) visible.add(e.target);
            else if (!e.isIntersecting) visible.delete(e.target);
          } else if (e.isIntersecting) visible.add(e.target);
          else visible.delete(e.target);
        }
        if (touch) {
          clearTimeout(fillTimer);
          fillTimer = setTimeout(fill, 150);
        } else {
          fill();
        }
      }, { threshold: touch ? [0, 0.5] : 0.35 });
      videos.forEach((v) => io.observe(v));

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          videos.forEach(pause);
          visible.clear();
          if (status) status.textContent = 'Canvases paused';
        } else {
          // re-observe so the observer re-reports what is on screen now
          videos.forEach((v) => { io.unobserve(v); io.observe(v); });
          if (status) status.textContent = '';
        }
      });
    }

    // Orbit sync. 2026-09-10, Jay: "coordinating and hue are two different
    // things ... I really want both", and "when you scroll down the image that
    // you're on on the carousel is on the wall, so you could shop right there" -
    // on the phone AND the desktop, "with no flicker".
    //
    // 2026-09-26 (Simon), Jay: the art row must match the carousel exactly -
    // left, centre and right - including when a colourway chip is clicked, and
    // "there will be a few exceptions that the live art does not equal the
    // number of garment variations". The row order is now read from the orbit's
    // LIVE slide list (DOM order; the colourway chips rewrite a slide's href),
    // not from the Wall Art collection order, so the tiles under the ring are
    // always the ring's own worlds. A colourway with no canvas of its own shows
    // the first canvas in its colourway group (#mb-colourway-groups). Tiles that
    // are not on the ring sit after it, in their Wall Art order.
    //
    // FLICKER, both sources removed (2026-09-11):
    //   1. no `is-shifting` dim on a carousel tap.
    //   2. warmWindow() fetches the clips around the incoming world before they
    //      arrive.
    // Tiles are never moved in the DOM, only their CSS `order` - a playing
    // <video> that is re-inserted reloads, and the loops must not blink.
    const orbit = document.querySelector('[data-mb-orbit]');
    const tiles = Array.from(root.querySelectorAll('a.mb-canvas[data-world]'));
    const grid = root.querySelector('.mb-canvas-grid');
    const MIRROR = true;
    if (MIRROR && orbit && tiles.length) {
      const tileWorlds = new Set(tiles.map((t) => t.dataset.world));
      let groups = [];
      try {
        const g = document.getElementById('mb-colourway-groups');
        groups = g ? JSON.parse(g.textContent || '[]') : [];
      } catch (err) { groups = []; }
      // Terrain shirts share their world's canvas tile.
      const canvasWorld = {
        'cosmic-mycelium-rave-terrain': 'cosmic-mycelium-rave',
        'chromatic-meltdown-terrain': 'chromatic-meltdown',
      };
      const handleOf = (slide) => {
        const href = slide.getAttribute('href') || '';
        const m = href.match(/\/products\/([^/?#]+)/);
        if (!m) return '';
        const h = canvasWorld[m[1]] || m[1];
        if (tileWorlds.has(h)) return h;
        const base = h.replace(/-terrain(-hoodie)?$/, '').replace(/-hoodie$/, '');
        if (tileWorlds.has(base)) return base;
        const grp = groups.find((list) => list.indexOf(base) !== -1);
        if (grp) {
          for (const w of grp) {
            if (tileWorlds.has(w)) return w;
            if (tileWorlds.has(w + '-terrain')) return w + '-terrain';
          }
        }
        return h;
      };
      const n = tiles.length;
      const columns = () => {
        if (!grid) return 1;
        const cs = getComputedStyle(grid).gridTemplateColumns || '';
        const count = cs.split(' ').filter((x) => x && x !== 'none').length;
        return count || 1;
      };
      const lead = () => Math.floor((columns() - 1) / 2);
      // Own warm(): the one in the observer block above is scoped to it. Under
      // prefers-reduced-motion no clip is ever attached, posters stand in.
      const warmClip = (tile) => {
        if (reduced || !tile) return;
        const v = tile.querySelector('video[data-clip]');
        if (!v) return;
        attachPoster(v);
        if (v.getAttribute('src')) return;
        v.preload = 'auto';
        v.src = v.dataset.clip;
        v.load();
      };
      // Ahead of the tile about to land under the carousel, plus a little behind
      // for a back-tap. Touch decodes far fewer at once (MAX_PLAYING is 3
      // there), so the window is smaller.
      // 2026-09-27 (Simon): on touch the window is one tile either side (three
      // clips, about 6 MB less on a phone's first load than the six it was).
      const warmWindow = (idx) => {
        const ahead = touch ? 1 : 8;
        const behind = touch ? 1 : 2;
        for (let k = -behind; k <= ahead; k += 1) warmClip(tiles[(idx + k + n) % n]);
      };
      let lastKey = '';
      const syncRow = () => {
        const slides = Array.from(orbit.querySelectorAll('[data-mb-orbit-slide]'));
        const centre = orbit.querySelector('[data-mb-orbit-slide][data-slot="c"]');
        if (!centre || !slides.length) return;
        const seq = slides.map(handleOf);
        const ci = slides.indexOf(centre);
        const l = lead();
        const key = seq.join(',') + '|' + ci + '|' + l;
        if (ci < 0 || key === lastKey) return;
        lastKey = key;
        const m = seq.length;
        const pos = new Map();
        seq.forEach((w, j) => { if (!pos.has(w)) pos.set(w, (j - ci + l + m) % m); });
        const idx = tiles.findIndex((t) => t.dataset.world === seq[ci]);
        if (idx >= 0) warmWindow(idx);
        // No dim and no timeout: `order` is cheap and the clips are already
        // warm, so the swap reads as a change of state rather than a flash.
        tiles.forEach((t, i) => {
          const p = pos.get(t.dataset.world);
          t.style.order = String(p === undefined ? m + i : p);
        });
      };
      new MutationObserver(syncRow).observe(orbit, { attributes: true, subtree: true, attributeFilter: ['data-slot', 'href'] });
      let resizeTimer = 0;
      window.addEventListener('resize', () => { clearTimeout(resizeTimer); resizeTimer = setTimeout(syncRow, 120); });
      syncRow();
    }

    // Friction layer: nothing to "save image as", nothing to drag out.
    root.addEventListener('contextmenu', (e) => {
      if (e.target.closest('.mb-canvas__stage')) e.preventDefault();
    });
    root.addEventListener('dragstart', (e) => e.preventDefault());

    // Back-navigation scroll restore (the ATTS trick). A heavy grid defeats
    // the browser's own back-forward cache and lands the visitor at the top.
    const key = `mb-canvas-scroll:${location.pathname}`;
    root.querySelectorAll('a.mb-canvas').forEach((a) => {
      a.addEventListener('click', () => {
        try { sessionStorage.setItem(key, String(window.scrollY)); } catch (err) {}
      });
    });
    const nav = performance.getEntriesByType('navigation')[0];
    let saved = null;
    try { saved = sessionStorage.getItem(key); } catch (err) {}
    if (saved && nav && nav.type === 'back_forward') {
      window.scrollTo({ top: parseInt(saved, 10), behavior: 'instant' });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
